// createRoomForm — Pure validation + body-construction logic for the
// Phase 6 Task 6 「1秒作成 UX」 settings form (CreateRoomPage).
//
// Why a separate module: the existing test convention in this package keeps
// logic testable without a DOM (see AdminTokenStore.test.ts header). By
// extracting the form's validation rules and outcome→message mapping here,
// CreateRoomPage's component test can stay light (or be written with RTL
// later) and the core rules are verified against a node-only Vitest.
//
// 確定方針 (Phase 6 Task 6 / consult 2026-05-04):
// - V. 設定ページの項目: ルーム名 (任意) / canvas size (4種から選択) /
//      owner password CB / email CB の 4 項目.
// - W. owner password 検証: 8〜128 文字 (UI 側), 確認入力なし.
// - X. ルーム名のデフォルト: 空欄 OK, server default ('お絵描きルーム') が吸収.
//
// **Note**: Server 側の owner password 上限は 256 (`OwnerPasswordSchema` in
// `createDispatcherApp.ts`). UI を 128 に絞っているのは UX/誤入力低減 (確定
// 方針 W) であって wire 制約ではない. wire は server 側の min/max を踏襲する.
//
// canvas size 選択肢 (2026-05 改訂): 旧版 4 種 (小 710×380 / 標準 800×450 /
// 中 880×450 / 大 1120×450) は aspect ratio が現代の画面と合わないため、
// **正方形 3 種 (小 1000×1000 / 標準 1500×1500 / 大 2000×2000) +
// カスタム数値入力 (500〜4000)** に再構成. Default = 標準 (1500×1500、WQHD
// 画面で開いて余白が目立たないライン、ユーザ実機確認 2026-05-08).
// server / dispatcher 側 (Room.ts / CreateRoom.ts / dispatcher schema) も
// 同じ範囲で更新 (CANVAS_MIN/MAX = 500/4000).

import { MAX_ENTRY_PASSWORD_LENGTH, normalizeEntryPassword } from '@oekaki/protocol';

import type {
  CreateRoomInput,
  CreateRoomOutcome,
} from '../infrastructure/dispatcher/DispatcherClient.js';
import { entryVisibilitySettingsForCreate } from './entryVisibilityFields.js';
import { formatMs } from './uiFeedback.js';

/* -----------------------------------------------------------------------------
 * Constants — re-exported for the React component to render labels / max-length
 * --------------------------------------------------------------------------- */

/** Server 側の `name.trim().max(128)`. UI も 128 を超えさせない. */
export const MAX_ROOM_NAME_LENGTH = 128;

/** Phase 6 Task 6 確定方針 W. */
export const OWNER_PASSWORD_MIN_LENGTH = 8;
export const OWNER_PASSWORD_MAX_LENGTH = 128;

/** Server 側 `EmailSchema.max(320)` と一致. */
export const OWNER_EMAIL_MAX_LENGTH = 320;

/**
 * 入室「あいことば」(owner-vs-entry-password-2026-06-13.md). `@oekaki/protocol`
 * の `MAX_ENTRY_PASSWORD_LENGTH` (1〜64 文字) を re-export — UI の `maxLength`
 * 属性・validation で使う. `MIN` は 1 文字 (= 非空文字列なら自動的に満たす)
 * なので下限バリデーションは不要 (空欄 = 未設定 = optional).
 */
export const ENTRY_PASSWORD_MAX_LENGTH = MAX_ENTRY_PASSWORD_LENGTH;

/* -----------------------------------------------------------------------------
 * Canvas size presets (2026-05 改訂、正方形 3 種 + カスタム入力)
 * --------------------------------------------------------------------------- */

/**
 * Stable id for the canvas-size selector. `'small' | 'standard' | 'large'`
 * map to `CANVAS_SIZE_PRESETS`; `'custom'` indicates the user entered free
 * width/height in the inputs and `customCanvasWidth` / `customCanvasHeight`
 * on the form values are authoritative.
 */
export type CanvasSizeSelectionId = 'small' | 'standard' | 'large' | 'custom';

export interface CanvasSizePreset {
  /** Stable id used in form value selection (preset-only — `'custom'` not present). */
  readonly id: Exclude<CanvasSizeSelectionId, 'custom'>;
  /** UI label, 「小」「標準」「大」. */
  readonly label: string;
  readonly width: number;
  readonly height: number;
}

export const CANVAS_SIZE_PRESETS: readonly CanvasSizePreset[] = [
  { id: 'small', label: '小', width: 1000, height: 1000 },
  { id: 'standard', label: '標準', width: 1500, height: 1500 },
  { id: 'large', label: '大', width: 2000, height: 2000 },
] as const;

/** Default = 標準 (server 側 `DEFAULT_ROOM_SETTINGS` と一致). */
export const DEFAULT_CANVAS_PRESET_ID: CanvasSizePreset['id'] = 'standard';

/** Custom canvas dimension bounds (matches server `CANVAS_MIN/MAX`). */
export const CANVAS_DIMENSION_MIN = 500;
export const CANVAS_DIMENSION_MAX = 4000;

export const findCanvasPreset = (id: string): CanvasSizePreset | null =>
  CANVAS_SIZE_PRESETS.find((p) => p.id === id) ?? null;

/* -----------------------------------------------------------------------------
 * Form values (mirrored 1:1 by React component state)
 * --------------------------------------------------------------------------- */

export interface CreateRoomFormValues {
  /** trim 前. submit 時 trim. 空文字は server default に吸収させる. */
  readonly roomName: string;
  /** Stable selector id. `'custom'` means use customCanvasWidth/Height. */
  readonly canvasSizeId: CanvasSizeSelectionId;
  /** Custom canvas width (used iff `canvasSizeId === 'custom'`). */
  readonly customCanvasWidth: number;
  /** Custom canvas height (used iff `canvasSizeId === 'custom'`). */
  readonly customCanvasHeight: number;
  /** "オーナーパスワードを設定する" CB. true なら password 必須. */
  readonly ownerPasswordEnabled: boolean;
  /** plain text. trim しない (passphrase は前後空白も意味あり得る). */
  readonly ownerPassword: string;
  /** "email を登録する" CB. true なら email 必須. */
  readonly ownerEmailEnabled: boolean;
  /** trim 前. submit 時 trim. */
  readonly ownerEmail: string;
  /**
   * entry-preview-members-2026-07-21 案 A: 入室画面の表示 2 フラグ.
   * デフォルト On。POST body への載せ方 (On = キー省略 / Off = false) は
   * `entryVisibilitySettingsForCreate` (application/entryVisibilityFields.ts)
   * に集約 — 文言・デフォルト解釈も同モジュールが正.
   */
  readonly entryShowPreview: boolean;
  readonly entryShowMembers: boolean;
  /**
   * 入室「あいことば」(owner-vs-entry-password-2026-06-13.md). オーナー
   * パスワードとは別物 — チェックボックスなし、常に見えている 1 個の任意
   * フィールド (空欄 = 未設定). 1〜64 文字、前後空白は trim しない
   * (passphrase 性、ownerPassword と同じ流儀).
   */
  readonly entryPassword: string;
}

export const INITIAL_FORM_VALUES: CreateRoomFormValues = {
  roomName: '',
  canvasSizeId: DEFAULT_CANVAS_PRESET_ID,
  customCanvasWidth: 1500,
  customCanvasHeight: 1500,
  ownerPasswordEnabled: false,
  ownerPassword: '',
  ownerEmailEnabled: false,
  ownerEmail: '',
  entryShowPreview: true,
  entryShowMembers: true,
  entryPassword: '',
};

/* -----------------------------------------------------------------------------
 * Validation
 * --------------------------------------------------------------------------- */

export type FormFieldError =
  | 'room-name-too-long'
  | 'owner-password-too-short'
  | 'owner-password-too-long'
  | 'owner-email-invalid'
  | 'canvas-dimension-too-small'
  | 'canvas-dimension-too-large'
  | 'canvas-dimension-not-integer'
  | 'entry-password-too-long';

export interface FormValidationOk {
  readonly outcome: 'ok';
  readonly input: CreateRoomInput;
}

export interface FormValidationFailed {
  readonly outcome: 'failed';
  /** field name → error code. UI 側で日本語にマップ. */
  readonly errors: Partial<Record<keyof CreateRoomFormValues, FormFieldError>>;
}

export type FormValidationOutcome = FormValidationOk | FormValidationFailed;

/**
 * Per-field validation for a custom canvas dimension. Used by `validateForm`
 * at submit time, and exposed for callers that want blur-time field error
 * surfacing (CreateRoomPage currently validates at submit, but the helper
 * is stable for future UX improvements).
 */
export type CustomCanvasDimensionOutcome = 'ok' | 'too-small' | 'too-large' | 'not-integer';

export const validateCustomCanvasDimension = (n: number): CustomCanvasDimensionOutcome => {
  if (!Number.isFinite(n) || !Number.isInteger(n)) return 'not-integer';
  if (n < CANVAS_DIMENSION_MIN) return 'too-small';
  if (n > CANVAS_DIMENSION_MAX) return 'too-large';
  return 'ok';
};

const customDimErrorCode = (
  outcome: Exclude<CustomCanvasDimensionOutcome, 'ok'>,
): FormFieldError => {
  switch (outcome) {
    case 'too-small':
      return 'canvas-dimension-too-small';
    case 'too-large':
      return 'canvas-dimension-too-large';
    case 'not-integer':
      return 'canvas-dimension-not-integer';
  }
};

/**
 * 簡易 email regex. server 側は zod `.email()` で本格チェックするので、
 * ここは UI 早期 reject 用の軽量チェック (false negative 寄り = 通せば
 * server が落とす). 確定方針 (consult): UI 側は zod を必須にせず軽量で OK
 * (このモジュール 1 ファイル内自前で十分).
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateForm = (values: CreateRoomFormValues): FormValidationOutcome => {
  const errors: Partial<Record<keyof CreateRoomFormValues, FormFieldError>> = {};
  const trimmedRoomName = values.roomName.trim();
  if (trimmedRoomName.length > MAX_ROOM_NAME_LENGTH) {
    errors.roomName = 'room-name-too-long';
  }

  if (values.ownerPasswordEnabled) {
    // 前後空白を含めた raw 文字列で長さ判定 (passphrase の空白は意味あり).
    if (values.ownerPassword.length < OWNER_PASSWORD_MIN_LENGTH) {
      errors.ownerPassword = 'owner-password-too-short';
    } else if (values.ownerPassword.length > OWNER_PASSWORD_MAX_LENGTH) {
      errors.ownerPassword = 'owner-password-too-long';
    }
  }

  // 入室あいことば: チェックボックス無し、空欄なら未設定 (optional) として
  // 素通し. **正規化後 (NFKC + trim) の長さ**で判定する — 一部の文字は NFKC
  // で文字数が変わり得るので、raw の長さで見ると server 側 (正規化後で判定)
  // と判定がずれる. 全角スペースだけの入力は正規化後に空文字列になり
  // 「未設定」と同じ扱いになる (下の input 組み立て箇所も参照).
  const normalizedEntryPassword = normalizeEntryPassword(values.entryPassword);
  if (normalizedEntryPassword.length > ENTRY_PASSWORD_MAX_LENGTH) {
    errors.entryPassword = 'entry-password-too-long';
  }

  const trimmedEmail = values.ownerEmail.trim();
  if (values.ownerEmailEnabled) {
    if (trimmedEmail.length === 0 || trimmedEmail.length > OWNER_EMAIL_MAX_LENGTH) {
      errors.ownerEmail = 'owner-email-invalid';
    } else if (!EMAIL_RE.test(trimmedEmail)) {
      errors.ownerEmail = 'owner-email-invalid';
    }
  }

  // Custom canvas dimension validation (only when the custom selector is active).
  if (values.canvasSizeId === 'custom') {
    const w = validateCustomCanvasDimension(values.customCanvasWidth);
    if (w !== 'ok') {
      errors.customCanvasWidth = customDimErrorCode(w);
    }
    const h = validateCustomCanvasDimension(values.customCanvasHeight);
    if (h !== 'ok') {
      errors.customCanvasHeight = customDimErrorCode(h);
    }
  }

  if (Object.keys(errors).length > 0) {
    return { outcome: 'failed', errors };
  }

  // Resolve dimensions: custom uses the typed values, presets use the table.
  let canvasWidth: number;
  let canvasHeight: number;
  if (values.canvasSizeId === 'custom') {
    canvasWidth = values.customCanvasWidth;
    canvasHeight = values.customCanvasHeight;
  } else {
    const preset = findCanvasPreset(values.canvasSizeId);
    // Should be unreachable — `canvasSizeId` is constrained by the radio UI to
    // CanvasSizeSelectionId. Guard regardless to avoid silent broken submits.
    if (preset === null) {
      return {
        outcome: 'failed',
        errors: {},
      };
    }
    canvasWidth = preset.width;
    canvasHeight = preset.height;
  }

  const input: CreateRoomInput = {
    // 空文字は送らない (server default 吸収のため).
    ...(trimmedRoomName.length > 0 ? { name: trimmedRoomName } : {}),
    // entry 2 フラグは On (デフォルト) ならキー省略 = settings JSON に書かれず
    // undefined = On のセマンティクス維持. Off のときだけ false を送る.
    settings: { canvasWidth, canvasHeight, ...entryVisibilitySettingsForCreate(values) },
    ...(values.ownerPasswordEnabled ? { ownerPassword: values.ownerPassword } : {}),
    ...(values.ownerEmailEnabled ? { ownerEmail: trimmedEmail } : {}),
    // 入室あいことば: 正規化後が非空のときだけ送る (空欄 = 未設定、キー自体を
    // 省略). 送る値も正規化済みのものにする (server 側でも同じ正規化を通すが、
    // 二重適用してもべき等なので安全 — normalizeEntryPassword のコメント参照).
    ...(normalizedEntryPassword.length > 0 ? { entryPassword: normalizedEntryPassword } : {}),
  };

  return { outcome: 'ok', input };
};

/* -----------------------------------------------------------------------------
 * Submission outcome → user-facing message mapping
 * --------------------------------------------------------------------------- */

/**
 * Render-friendly message bundle for the CreateRoomPage submit handler.
 *
 * `kind`:
 *  - `error`   = ブロッキングエラー、ルームは作成できていない (再試行 OK).
 *  - `partial` = 部分成功 (room は作成済 + email 送信失敗、UI は warning 扱い).
 *
 * Phase 6 Task 6.6-B: server (Task 6.5) は email 送信失敗でも 200 +
 * `emailVerificationSent: false` を返すように変更済. 部分成功は **ok 経路の中**
 * で発火する (CreateRoomPage が `ownerEmailEnabled && !emailVerificationSent`
 * を見て分岐する). このヘルパは「ok でない」失敗 outcome のメッセージのみを返す.
 */
export interface CreateRoomFailureMessage {
  readonly kind: 'error' | 'partial';
  /** 1 行で出せるユーザ向け文言. */
  readonly message: string;
  /** デバッグ用 detail (ErrorScreen 等の `detail` 表示に使える). */
  readonly detail?: string;
}

/**
 * Phase 6 Task 6.6-B: ok 経路の partial 成功 (email 送信失敗) 専用メッセージ.
 * `messageForFailure` は ok でない outcome のみ扱うので、ok + 部分失敗は
 * このヘルパで生成する.
 */
export const partialEmailFailureMessage = (): CreateRoomFailureMessage => ({
  kind: 'partial',
  message:
    'ルームは作成されましたが、確認メールの送信に失敗しました。後で管理画面から再送できます。',
});

/**
 * Map a non-ok createRoom outcome to a user-facing message.
 * (`'ok'` is excluded by the caller — fail compile if a new outcome arrives.)
 */
export const messageForFailure = (
  outcome: Exclude<CreateRoomOutcome, { outcome: 'ok' }>,
): CreateRoomFailureMessage => {
  switch (outcome.outcome) {
    case 'no-server-available':
      return {
        kind: 'error',
        message: 'サーバが満員です。しばらくしてから再度お試しください。',
      };
    case 'rate-limited':
      return {
        kind: 'error',
        message: `ルーム作成上限に達しました (${formatMs(outcome.retryAfterMs)} 後に再試行してください)。`,
        detail: `retryAfterMs=${outcome.retryAfterMs}`,
      };
    case 'error':
      switch (outcome.cause) {
        case 'network':
          return {
            kind: 'error',
            message: 'ネットワークエラーです。接続を確認してください。',
          };
        case 'invalid':
          return {
            kind: 'error',
            message: 'サーバの応答が不正です。時間を置いて再度お試しください。',
            detail: outcome.status !== undefined ? `HTTP ${outcome.status}` : undefined,
          };
        case 'http-status':
          return {
            kind: 'error',
            message: `サーバエラーが発生しました (HTTP ${outcome.status ?? '?'})。`,
            detail: outcome.status !== undefined ? `HTTP ${outcome.status}` : undefined,
          };
      }
  }
};

/* -----------------------------------------------------------------------------
 * Field error → display string
 * --------------------------------------------------------------------------- */

export const fieldErrorText = (err: FormFieldError): string => {
  switch (err) {
    case 'room-name-too-long':
      return `ルーム名は ${MAX_ROOM_NAME_LENGTH} 文字以内で入力してください。`;
    case 'owner-password-too-short':
      return `オーナーパスワードは ${OWNER_PASSWORD_MIN_LENGTH} 文字以上で入力してください。`;
    case 'owner-password-too-long':
      return `オーナーパスワードは ${OWNER_PASSWORD_MAX_LENGTH} 文字以内で入力してください。`;
    case 'owner-email-invalid':
      return 'メールアドレスの形式が正しくありません。';
    case 'canvas-dimension-too-small':
      return `${CANVAS_DIMENSION_MIN} 以上の値を入力してください。`;
    case 'canvas-dimension-too-large':
      return `${CANVAS_DIMENSION_MAX} 以下の値を入力してください。`;
    case 'canvas-dimension-not-integer':
      return '整数を入力してください。';
    case 'entry-password-too-long':
      return `あいことばは ${ENTRY_PASSWORD_MAX_LENGTH} 文字以内で入力してください。`;
  }
};
